import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { ChevronDown } from 'lucide-react';

const FAQS = [
  {
    question: 'Can Sortio organize family photos by year and month?',
    answer:
      "Yes. Point Sortio at your Pictures folder (or iCloud Photos, or an external drive) and ask in plain English: \"organize photos into folders by year and month based on date taken.\" Sortio reads the EXIF date taken from each image, falls back to file metadata when EXIF is missing, and creates a clean 2014/03, 2014/04 style folder tree. You preview every move before it runs.",
  },
  {
    question: 'How do I consolidate duplicate photo galleries from a wedding photographer?',
    answer:
      "This is a common one. The photographer drops the same gallery in four batches called \"All Images (Print)\", \"(Print) 2\", \"(Print) 3\", and \"photos-6_of_10\". Sortio's dedup workflow detects identical and near-identical files across folders, lets you keep the highest-quality copy, and merges everything into a single wedding folder. Then ask Sortio to file by event or year and you're done.",
  },
  {
    question: 'Does Sortio handle HEIC photos from iPhone alongside JPGs?',
    answer:
      "Yes. Sortio treats HEIC and JPG the same way, reading EXIF dates, geolocation, and content. You don't have to convert anything. If you do want HEICs converted to JPG, that's a one-line prompt: \"convert HEIC photos to JPG in this folder.\" Sortio runs the conversion locally on your Mac.",
  },
  {
    question: 'Does Sortio work with iCloud Photos?',
    answer:
      "Yes, as long as you have \"Download Originals to this Mac\" enabled in iCloud Photos settings. Sortio sees iCloud Photos as a regular folder on your filesystem and treats it like any other Pictures library. Any reorganizing you do syncs back through iCloud to your other devices automatically.",
  },
  {
    question: 'Can Sortio organize tax documents by year?',
    answer:
      "Absolutely. Ask: \"file tax documents by year, separate W-2s, 1099s, receipts, and final returns.\" Sortio reads each PDF, identifies the tax year and document type, and files into Tax/2023/W-2, Tax/2023/1099, Tax/2023/Receipts, and so on. The Rule Builder lets you save this as a repeatable workflow for next year.",
  },
  {
    question: 'I have tax returns going back to 1997. Can Sortio handle multi-decade archives?',
    answer:
      "Yes. The free tier includes a one-time AI trial allowance, enough to organize a single year and judge the results. For a 27-year archive with thousands of receipts and forms, AI sort processes up to 5,000 files in a single run, and Pro's monthly AI allowance is sized for that kind of backlog. Sortio handles scanned PDFs, photographed receipts, and digital downloads the same way: it reads the content to determine the year and category.",
  },
  {
    question: 'How does Sortio file house build or renovation paperwork?',
    answer:
      "Sortio recognizes plans, quotes, invoices, council approvals, door and window specs, and contractor correspondence. Try a prompt like: \"organize the Mitchell Street House folder into Plans, Quotes, Approvals, Doors, Windows, Invoices, and Contractor Correspondence.\" If you have files from multiple contractors, Sortio can sub-fold by contractor name as well.",
  },
  {
    question: 'Can Sortio sort family member documents like passports, PAN cards, and certificates?',
    answer:
      "Yes. Sortio reads document content to identify the person and document type. A prompt like \"file family documents by person, then by type\" produces folders like Family/Mom/Passport, Family/Dad/PAN Card, Family/Child/Birth Certificate. Sortio reads files locally on your Mac. In Offline mode (a local LLM like Ollama) or with your own API key (BYOK), the document contents never leave your machine. By default Sortio uses our cloud (OpenAI) to plan the sort and sends filenames and folder paths; the contents of these identity documents are sent only if you enable a content-based feature.",
  },
  {
    question: 'Will Sortio rename PDFs based on what they contain?',
    answer:
      "Yes, this is one of the most loved features. Instead of scan_001.pdf and IMG_2847.pdf, Sortio renames files based on content: 2023-04-12 Electric Bill Origin Energy.pdf, 2024-01 Tax Return Final.pdf, Mitchell St Window Quote Stegbar.pdf. You set the naming pattern once and Sortio applies it to every file in the sort.",
  },
  {
    question: 'Can Sortio remove duplicate or blurry photos?',
    answer:
      "Sortio detects duplicates by content hash and near-duplicates by visual similarity, and surfaces them in a review screen before deletion. For blurry photos, Sortio can flag low-sharpness images so you can review and remove them. Nothing is deleted automatically. You always confirm before anything moves to the trash.",
  },
  {
    question: 'When should I upgrade from Free to Pro?',
    answer:
      "Start on Free. The one-time AI trial allowance covers plenty of trial runs, and rule-based sorting is free and unlimited, which handles most weekly cleanups on its own. Upgrade to Pro ($14.99/mo or $99/yr) when you're tackling a large one-time project (thousands of old photos, a decade of tax docs) or when you want auto-sort to keep your Downloads folder tidy without thinking about it.",
  },
  {
    question: 'Can Sortio organize MOV videos from old phones and home movies?',
    answer:
      "Yes. Sortio sorts video files by date created, file type, or content category. A prompt like \"organize videos into folders by year and month\" works on MOV, MP4, AVI, and other common formats. For older home movies that have lost their metadata, Sortio can prompt you for an approximate date range and file accordingly.",
  },
  {
    question: 'How do I clean up a Downloads folder with thousands of files going back years?',
    answer:
      "Point Sortio at your Downloads folder, then say something like: \"sort these into Documents, Photos, Videos, Software, Receipts, and Other. Then file by year based on download date.\" Sortio handles the long tail (random installer DMGs, screenshot images, email attachments, PDFs) by reading what each file actually is. Pro is recommended if you're starting with 1,000+ files.",
  },
  {
    question: 'I use my Mac for both work and personal files. Can Sortio separate them?',
    answer:
      "Yes. Sortio Spaces let you set up two completely separate sort workflows, one for Work and one for Personal, each with its own rules and target folders. A prompt like \"split this folder into Work and Personal based on content, sender, or filename\" produces a clean separation. Spaces means you can run a Work cleanup without it ever touching your family archive.",
  },
  {
    question: 'Does Sortio handle TAFE or school assignments and coursework?',
    answer:
      "Yes. Students and parents use Sortio to organize coursework by subject, unit, and semester. A prompt like \"file TAFE Work Health Safety assignments by unit and assessment type\" produces a clean structure like TAFE/WHS/Unit 1/Assessment 1, Unit 1/Assessment 2, and so on. Same approach works for K-12 school PDFs and project files.",
  },
];

const FAQItem = ({ faq, isOpen, onToggle }) => (
  <div className="bg-apple-800/40 backdrop-blur-sm rounded-2xl border border-apple-700/50 overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-apple-800/60 transition-colors"
    >
      <h3 className="text-base sm:text-lg font-medium text-apple-50 pr-4">{faq.question}</h3>
      <ChevronDown
        className={`text-sortio-primary flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        size={20}
      />
    </button>
    {isOpen && (
      <div className="px-6 pb-5 text-apple-200 text-sm sm:text-base leading-relaxed">
        {faq.answer}
      </div>
    )}
  </div>
);

const PersonalArchivistsFAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer,
      },
    })),
  };

  return (
    <section id="faq" className="my-20">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="text-center mb-12">
        <h2 className="text-3xl sm:text-4xl font-semibold mb-4 tracking-tight text-apple-50">
          Frequently Asked Questions
        </h2>
        <p className="text-apple-300 text-lg font-light max-w-2xl mx-auto">
          Real questions from real households. If yours isn't here, the contact form below reaches
          us directly.
        </p>
      </div>

      <div className="max-w-3xl mx-auto space-y-3">
        {FAQS.map((faq, i) => (
          <FAQItem
            key={i}
            faq={faq}
            isOpen={openIndex === i}
            onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
          />
        ))}
      </div>
    </section>
  );
};

export default PersonalArchivistsFAQ;
